import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { AuthAPI } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';

const EmailVerificationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const token = searchParams.get('token');

    if (!token) {
      setError('Invalid verification link. Please check your email and try again.');
      setLoading(false);
      return;
    }

    const handleVerification = async () => {
      try {
        await AuthAPI.verifyEmail(token);
        setSuccess(true);

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Email verification failed. Please try again.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    handleVerification();
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="page-container">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <div className="text-center">
            <LoadingSpinner size="large" message="Verifying your email address..." />
            <p className="mt-3 text-muted">Please wait while we verify your account.</p>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="page-container">
        <div className="d-flex justify-content-center">
          <div className="card" style={{ maxWidth: '500px', width: '100%' }}>
            <div className="card-header text-center">
              <h2 className="card-title">✅ Email Verified!</h2>
            </div>
            <div className="card-body text-center">
              <div className="alert alert-success">
                <strong>Success!</strong> Your email address has been verified successfully.
              </div>
              
              <p>Your account is now active and you can access all features.</p>
              
              <div className="mb-4">
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎉</div>
                <h4>Welcome to KeeDocs!</h4>
                <p className="text-muted">
                  You now have access to unlimited document storage, advanced sharing features, 
                  and detailed analytics.
                </p>
              </div>

              <p className="text-muted">
                Redirecting to sign in in a few seconds...
              </p>

              <div className="d-flex justify-content-center" style={{ gap: '10px' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => navigate('/login')}
                >
                  Sign In
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="d-flex justify-content-center">
        <div className="card" style={{ maxWidth: '500px', width: '100%' }}>
          <div className="card-header text-center">
            <h2 className="card-title">❌ Verification Failed</h2>
          </div>
          <div className="card-body">
            <ErrorMessage 
              message={error} 
              onDismiss={() => setError('')} 
            />

            <div className="text-center mt-4">
              <h5>What can you do?</h5>
              <ul className="list-unstyled">
                <li>✓ Check if the verification link is complete</li>
                <li>✓ Make sure you're using the latest email</li>
                <li>✓ Try requesting a new verification email</li>
              </ul>

              <div className="d-flex justify-content-center" style={{ gap: '10px', marginTop: '20px' }}>
                <Link to="/login" className="btn btn-primary">
                  Back to Login
                </Link>
                <Link to="/register" className="btn btn-secondary">
                  Register Again
                </Link>
              </div>

              <div className="mt-4">
                <p className="text-muted">
                  Need help? <Link to="/contact" style={{ color: '#007bff' }}>Contact Support</Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;